<template>
  <div
    :class="{
      small,
      'options-filter-container': true,
    }"
  >
    <div class="content">
      <div class="body3 text-truncate" style="min-width: 50px">
        {{ $t('filter_by') }}:
      </div>

      <slot name="prepend"></slot>

      <div
        class="d-flex align-center"
        :style="{
          maxWidth: 'calc(100% - 240px)',
          minWidth: '100px',
        }"
      >
        <v-slide-group
          show-arrows
          next-icon="$keyboard_arrow_right"
          prev-icon="$keyboard_arrow_left"
        >
          <v-slide-item v-for="(filter, index) of value" :key="index">
            <component
              :is="filter.type + '-type'"
              :id="filter.id"
              :icon="filter.icon"
              :text="filter.text"
              :value="filter.value"
              :disabled="filter.disabled"
              :configuration="filter.configuration"
              :color="primaryColor"
              :icon-color="defineIconColor(filter)"
              @input="onFilterInput"
            />
          </v-slide-item>
        </v-slide-group>
      </div>

      <div class="append-container ml-auto">
        <div v-if="hasSelectedFilters" class="delete-all-filters">
          <z-tooltip open-delay="500">
            <template #activator="{on, attrs}">
              <z-icon
                color="var(--black)"
                :size="small ? 12 : 15"
                v-bind="attrs"
                v-on="on"
                @click="onDeleteFilters"
              >
                $delete_rounded
              </z-icon>
            </template>

            {{ $t('remove_all_filters') }}
          </z-tooltip>
        </div>

        <z-filter-management
          v-if="showManageFilters"
          :small="small"
          :filter-groups="filterGroups"
          @delete-group="onDeleteGroup"
          @save-group="onSaveFiltersGroup"
          @save-new-group="onSaveNewFiltersGroup"
          @include-filters="onIncludeFilters"
        ></z-filter-management>
        <slot name="append"> </slot>
      </div>
    </div>
  </div>
</template>

<script>
import BooleanType from './menus/BooleanType.vue'
import SelectType from './menus/SelectType.vue'
import RangeType from './menus/RangeType.vue'
import MultipleType from './menus/MultipleType.vue'
import DateType from './menus/DateType.vue'

export default {
  name: 'ZOptionsFilter',
  components: {
    BooleanType,
    SelectType,
    RangeType,
    MultipleType,
    DateType,
  },

  i18n: {
    messages: {
      en: {
        filter_by: 'Filter by',
        remove_all_filters: 'Remove all filters',
      },
      pt: {
        filter_by: 'Filtrar por',
        remove_all_filters: 'Remover todos os filtros',
      },
      es: {
        filter_by: 'Filtrar por',
        remove_all_filters: 'Eliminar todos los filtros',
      },
      fr: {
        filter_by: 'Filtrer par',
        remove_all_filters: 'Supprimer tous les filtres',
      },
      tr: {
        filter_by: 'Filtrele',
        remove_all_filters: 'Tüm filtreleri kaldır',
      },
    },
  },

  props: {
    /**
     * @typedef {Object} FilterItem
     * @property {string} id
     * @property {string} text
     * @property {string} icon
     * @property {unknown} value
     * @property {Boolean} disabled
     * @property {'select' | 'range' | 'boolean' | 'multiple'} type
     * @property {Object} configuration
     */

    /**
     * @type {Array<FilterItem>}
     */
    value: {
      type: Array,
      default: () => [],
    },

    showManageFilters: {
      type: Boolean,
      default: true,
    },

    /**
     * @type {Array<{name: string, filters: Array<{text: string, value: string}>}>}
     */
    filterGroups: {
      type: Array,
      default: () => [],
    },

    primaryColor: {
      type: String,
      default: 'var(--primary)',
    },

    small: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    hasSelectedFilters() {
      return this.value.filter((item) => item.value).length > 0
    },
  },

  methods: {
    onIncludeFilters({filters}) {
      const updatedItems = this.value.map((item) => {
        const filter = filters.find((filter) => filter.id === item.id)

        if (filter) {
          return {
            ...item,
            value: filter.value,
          }
        }

        return item
      })

      this.$emit('input', updatedItems)
    },

    onDeleteFilters() {
      const updatedItems = this.value.map((item) => ({
        ...item,
        value: item.type === 'date' || item.type === 'multiple' ? [] : null,
      }))

      this.$emit('input', updatedItems)
    },

    onFilterInput(value) {
      const updatedItems = this.value.map((item) => {
        if (item.id === value.id) {
          return {
            ...item,
            value: value.value,
          }
        }

        return item
      })

      this.$emit('input', updatedItems)
    },

    onSaveFiltersGroup(groupName) {
      if (this.value.every((item) => !item.value)) {
        console.warn(
          'z-options-filter: Não é possível salvar um grupo de filtros vazio'
        )
        return
      }

      //formata os dados de filtros para o formato de group
      const filters = this.value
        .map((item) => ({
          id: item.id,
          text: item.text,
          value: item.value,
        }))
        .filter((item) =>
          Array.isArray(item.value) ? item.value.length > 0 : item.value != null
        )

      this.$emit('save-group', {name: groupName, filters})
    },

    onSaveNewFiltersGroup(groupName) {
      if (this.value.every((item) => !item.value)) {
        console.warn(
          'z-options-filter: Não é possível salvar um grupo de filtros vazio'
        )
        return
      }

      //formata os dados de filtros para o formato de group
      const filters = this.value
        .map((item) => ({
          id: item.id,
          text: item.text,
          value: item.value,
        }))
        .filter((item) =>
          Array.isArray(item.value) ? item.value.length > 0 : item.value != null
        )

      this.$emit('save-new-group', {name: groupName, filters})
    },

    onDeleteGroup(group) {
      this.$emit('delete-group', group)
    },
    defineIconColor(filter) {
      if (filter.iconColor) {
        return filter.iconColor
      }

      if (Array.isArray(filter.value)) {
        return filter.value.length > 0 ? 'white' : 'black'
      }

      return filter.value ? 'white' : 'black'
    },
  },
}
</script>

<style scoped lang="scss">
* {
  line-height: 20px;
}

:deep(.zoox-chip) {
  border: none !important;
}

.options-filter-container {
  width: 100%;
  height: 31px;
  max-height: 32px;
  background-color: #fff;
  border-radius: 15px;
  padding: 5px 15px;
  background: var(--black-100);
  min-width: 0 !important;

  .content {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 5px;
    width: 100%;
    flex-wrap: nowrap;

    .append-container {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 5px;

      .delete-all-filters {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid var(--black);
        border-radius: 50%;
        width: 21px;
        height: 21px;
      }
    }
  }
  /** 
  Efeito de gradiente nos botões de navegação do slide
*/
  :deep(.v-slide-group__prev) {
    position: relative;
    justify-content: center;
    margin-right: 2px;
    min-width: 21px !important;
    max-width: 21px !important;
    max-height: 21px !important;
    border: 1px solid var(--black);
    border-radius: 50%;

    i {
      color: var(--black);
      font-size: 20px;
    }

    &::before {
      content: '';
      position: absolute;
      z-index: 1;
      top: -1px;
      left: 21px;
      pointer-events: none;
      background: linear-gradient(
        60deg,
        #ddd 20.83%,
        rgba(217, 217, 217, 0.2) 100%
      );
      width: 25px;
      height: 21px;
    }

    &.v-slide-group__prev--disabled {
      display: none;
    }
  }

  :deep(.v-slide-group__next) {
    justify-content: center;
    margin-left: 2px;
    min-width: 21px !important;
    max-width: 21px !important;
    max-height: 21px !important;
    border: 1px solid var(--black);
    border-radius: 50%;

    &::before {
      content: '';
      position: absolute;
      z-index: 1;
      top: 0px;
      right: 21px;
      pointer-events: none;
      background: linear-gradient(
        270deg,
        #ddd 20.83%,
        rgba(217, 217, 217, 0.2) 100%
      );
      width: 25px;
      height: 21px;
    }

    i {
      color: var(--black);
      font-size: 20px;
    }

    &.v-slide-group__next--disabled {
      display: none !important;
    }
  }

  &.small {
    height: 24px;
    padding-top: 2px;
    padding-bottom: 2px;

    .delete-all-filters {
      height: 16px !important;
      width: 16px !important;
    }

    :deep(.v-slide-group__content) {
      padding-left: 5px;
      padding-right: 5px;
    }

    :deep(.v-slide-group__prev) {
      max-width: 16px !important;
      min-width: 16px !important;
      max-height: 16px !important;

      &::before {
        height: 16px;
        left: 17px;
      }
    }

    :deep(.v-slide-group__next) {
      max-width: 16px !important;
      min-width: 16px !important;
      max-height: 16px !important;

      &::before {
        height: 16px;
        right: 17px;
      }
    }

    :deep(.zoox-chip) {
      height: 16px !important;
    }

    :deep(i) {
      font-size: 12px;
    }
  }
}
</style>
